import type { LitElement } from "lit";

const defaults = new WeakMap<LitElement, Map<string, unknown>>();

/**
 * Snapshots reflected non-boolean property defaults on first render, then
 * restores them if they're externally set to null (e.g. by Turbo/idiomorph
 * removing attributes that Lit reflected from defaults).
 *
 * Call from `willUpdate(changedProperties)`.
 */
export function restoreReflectedDefaults(
  element: LitElement,
  changedProperties: Map<PropertyKey, unknown>
): void {
  if (!defaults.has(element)) {
    const snapshot = new Map<string, unknown>();
    const ctor = element.constructor as typeof LitElement;

    for (const [name, options] of ctor.elementProperties) {
      if (typeof name === "string" && options.reflect === true && options.type !== Boolean) {
        const value = (element as unknown as Record<string, unknown>)[name];
        if (value != null) {
          snapshot.set(name, value);
        }
      }
    }

    defaults.set(element, snapshot);
    return;
  }

  const snapshot = defaults.get(element)!;

  for (const [name] of changedProperties) {
    if (
      typeof name === "string" &&
      snapshot.has(name) &&
      (element as unknown as Record<string, unknown>)[name] == null
    ) {
      (element as unknown as Record<string, unknown>)[name] = snapshot.get(name);
    }
  }
}
